import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getForecastAdherence,
  getForecastAdherenceReport,
  getForecastDivisionDeptByVendor,
  getForecastDivisionDeptVendorByPart,
  getForecastLocations,
  getForecastPartDetails,
  getForecastVendorDetails,
} from "../api/forecast";
import { downloadReportForS3Link } from "../api/downloadFile";
import axios from "axios";

export const fetchForecastAdherence = createAsyncThunk(
  "forecast/fetchForecastAdherence",
  async (params) =>
    getForecastAdherence(
      params?.aggr,
      params?.forecastColumn,
      params?.startDate,
      params?.endDate,
      params?.division,
      params?.department,
      params?.vendor,
      params?.parts,
      params?.location,
      params?.page,
      params?.limit,
      params?.sort
    ).then((res) => res)
);

export const fetchForecastAdherenceReport = createAsyncThunk(
  "forecast/fetchForecastAdherenceReport",
  async (params) =>
    getForecastAdherenceReport(
      params?.startDate,
      params?.endDate,
      params?.forecastColumn,
      params?.division,
      params?.department,
      params?.vendor,
      params?.parts,
      params?.requestId
    )
      .then((res) => res)
      .catch((err) => err)
);

export const fetchForecastDivisionDeptVendorByPart = createAsyncThunk(
  "forecast/fetchForecastDivisionDeptVendorByPart",
  async (params) =>
    getForecastDivisionDeptVendorByPart(params?.parts).then((res) => res)
);

export const fetchForecastDivisionDeptByVendor = createAsyncThunk(
  "forecast/fetchForecastDivisionDeptByVendor",
  async (params) =>
    getForecastDivisionDeptByVendor(params?.vendor).then((res) => res)
);

export const fetchForecastLocations = createAsyncThunk(
  "forecast/fetchForecastLocations",
  async () => getForecastLocations().then((res) => res)
);

export const fetchForecastVendorDetails = createAsyncThunk(
  "forecast/fetchForecastVendorDetails",
  async (params) =>
    getForecastVendorDetails(
      params?.division,
      params?.department,
      params?.page,
      params?.search
    ).then((res) => res)
);

export const fetchForecastPartDetails = createAsyncThunk(
  "forecast/fetchForecastPartDetails",
  async (params) =>
    getForecastPartDetails(
      params?.department,
      params?.vendor,
      params?.page,
      params?.search
    ).then((res) => res)
);

export const fetchForecastSubFilterVendorDetails = createAsyncThunk(
  "forecast/fetchForecastSubFilterVendorDetails",
  async (params) =>
    getForecastVendorDetails(
      params?.division,
      params?.department,
      params?.page,
      params?.search
    ).then((res) => res)
);

export const fetchForecastSubFilterPartDetails = createAsyncThunk(
  "forecast/fetchForecastSubFilterPartDetails",
  async (params) =>
    getForecastPartDetails(
      params?.department,
      params?.vendor,
      params?.page,
      params?.search
    ).then((res) => res)
);

export const fetchDownloadReportS3Link = createAsyncThunk(
  "forecast/fetchDownloadReportS3Link",
  async (params) =>
    downloadReportForS3Link(params?.requestId).then((res) => res)
);

export const forecastSlice = createSlice({
  name: "forecast",
  initialState: {
    forecastAdherence: null,
    forecastAdherenceReport: null,
    loading: false,
    vendorDetails: null,
    vendorDetailsLoading: false,
    divisionDeptListByVendor: null,
    partDetails: null,
    partDetailsLoading: false,
    divisionDeptVendorListByPart: null,
    locationDetails: null,
    vendorSubFilterDetails: null,
    partSubFilterDetails: null,
    forecastReportError: false,
    forecastDownloadFileResponse: null,
  },
  reducers: {
    resetForecastReportError: (state, { payload }) => {
      state.forecastReportError = false;
    },
    resetForecastDownloadFileResponse: (state, { payload }) => {
      state.forecastDownloadFileResponse = false;
    },
  },
  extraReducers: {
    [fetchForecastAdherence.pending]: (state, { payload }) => {
      state.loading = true;
    },
    [fetchForecastAdherence.fulfilled]: (state, { payload }) => {
      state.forecastAdherence = payload;
      state.loading = false;
    },
    [fetchForecastAdherence.rejected]: (state, { payload }) => {
      state.loading = false;
    },
    [fetchForecastAdherenceReport.pending]: (state, { payload }) => {
      state.loading = true;
    },
    [fetchForecastAdherenceReport.fulfilled]: (state, { payload }) => {
      if (axios.isAxiosError(payload)) state.forecastReportError = true;
      else {
        state.forecastReportError = false;
        state.forecastAdherenceReport = payload;
      }
      state.loading = false;
    },
    [fetchForecastAdherenceReport.rejected]: (state, { payload }) => {
      state.forecastReportError = true;
      state.loading = false;
    },
    [fetchForecastVendorDetails.pending]: (state, { payload }) => {
      state.vendorDetailsLoading = true;
    },
    [fetchForecastVendorDetails.fulfilled]: (state, { payload }) => {
      state.vendorDetails = payload;
      state.vendorDetailsLoading = false;
    },
    [fetchForecastVendorDetails.rejected]: (state, { payload }) => {
      state.vendorDetailsLoading = false;
    },
    [fetchForecastSubFilterVendorDetails.pending]: (state, { payload }) => {
      state.vendorDetailsLoading = true;
    },
    [fetchForecastSubFilterVendorDetails.fulfilled]: (state, { payload }) => {
      state.vendorSubFilterDetails = payload;
      state.vendorDetailsLoading = false;
    },
    [fetchForecastSubFilterVendorDetails.rejected]: (state, { payload }) => {
      state.vendorDetailsLoading = false;
    },
    [fetchForecastDivisionDeptByVendor.pending]: (state, { payload }) => {
      state.divisionDetailsLoading = true;
    },
    [fetchForecastDivisionDeptByVendor.fulfilled]: (state, { payload }) => {
      state.divisionDeptListByVendor = payload;
      state.divisionDetailsLoading = false;
    },
    [fetchForecastDivisionDeptByVendor.rejected]: (state, { payload }) => {
      state.divisionDetailsLoading = false;
    },
    [fetchForecastPartDetails.pending]: (state, { payload }) => {
      state.partDetailsLoading = true;
    },
    [fetchForecastPartDetails.fulfilled]: (state, { payload }) => {
      state.partDetails = payload;
      state.partDetailsLoading = false;
    },
    [fetchForecastPartDetails.rejected]: (state, { payload }) => {
      state.partDetailsLoading = false;
    },
    [fetchForecastSubFilterPartDetails.pending]: (state, { payload }) => {
      state.partDetailsLoading = true;
    },
    [fetchForecastSubFilterPartDetails.fulfilled]: (state, { payload }) => {
      state.partSubFilterDetails = payload;
      state.partDetailsLoading = false;
    },
    [fetchForecastSubFilterPartDetails.rejected]: (state, { payload }) => {
      state.partDetailsLoading = false;
    },
    [fetchForecastDivisionDeptVendorByPart.pending]: (state, { payload }) => {
      state.divisionDetailsLoading = true;
    },
    [fetchForecastDivisionDeptVendorByPart.fulfilled]: (state, { payload }) => {
      state.divisionDeptVendorListByPart = payload;
      state.divisionDetailsLoading = false;
    },
    [fetchForecastDivisionDeptVendorByPart.rejected]: (state, { payload }) => {
      state.divisionDetailsLoading = false;
    },
    [fetchForecastLocations.fulfilled]: (state, { payload }) => {
      state.locationDetails = payload;
    },
    [fetchDownloadReportS3Link.fulfilled]: (state, { payload }) => {
      state.forecastDownloadFileResponse = payload;
    },
  },
});

export default forecastSlice.reducer;
export const { resetForecastReportError, resetForecastDownloadFileResponse } = forecastSlice.actions;
